// Bank Statement Analyzer workspace — the portal's access-scoped loan picker
// in front of the embedded Bank Statement Analyzer (BSA) service.

import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { AlertTriangle, ArrowLeft, RefreshCw, Search, X } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useLoanCenterScope } from '@/components/encompass/prospects/loanScope';
import { useAuth } from '@/contexts/useAuth';
import { isExtendedUser } from '@/types/auth';
import {
  ANALYZER_HEALTH_PATH,
  borrowerDisplayName,
  buildAnalyzerEmbedUrl,
  isAnalyzerHealthyResponse,
  loanOfficerDisplayName,
  type AnalyzerLoan
} from './analyzerLoans';
import { ANALYZER_LOAN_SEARCH_MIN_LENGTH, useAnalyzerLoans } from './useAnalyzerLoans';
import { AnalyzerEfolderBridge } from './AnalyzerEfolderBridge';

type AnalyzerAvailability = 'checking' | 'online' | 'offline';

function useAnalyzerAvailability() {
  const requestIdRef = useRef(0);
  const controllerRef = useRef<AbortController | null>(null);
  const [availability, setAvailability] = useState<AnalyzerAvailability>('checking');

  const check = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    const timeout = window.setTimeout(() => {
      controller.abort();
      if (requestId === requestIdRef.current) {
        setAvailability('offline');
      }
    }, 10000);
    controller.signal.addEventListener('abort', () => window.clearTimeout(timeout), { once: true });
    setAvailability('checking');
    let next: AnalyzerAvailability = 'offline';
    try {
      const response = await fetch(ANALYZER_HEALTH_PATH, { cache: 'no-store', signal: controller.signal });
      const body: unknown = await response.json();
      next = isAnalyzerHealthyResponse(response.status, body) ? 'online' : 'offline';
    } catch {
      next = 'offline';
    } finally {
      window.clearTimeout(timeout);
    }
    if (requestId === requestIdRef.current && !controller.signal.aborted) {
      setAvailability(next);
    }
  }, []);

  useEffect(() => {
    void check();
    return () => {
      requestIdRef.current += 1;
      controllerRef.current?.abort();
    };
  }, [check]);

  return { availability, check };
}

interface AnalyzerWorkspaceProps {
  onBack: () => void;
}

interface EmbeddedAnalyzerProps {
  loan: AnalyzerLoan;
  onChangeLoan: () => void;
}

/**
 * The embedded analyzer fills the window from wherever it starts down to the
 * bottom edge — measured, not assumed, so a portal top bar on narrow screens
 * never pushes it past the fold. BSA's embed mode keeps its own bottom bar
 * clear of the portal's floating quick-actions button.
 */
function EmbeddedAnalyzer({ loan, onChangeLoan }: EmbeddedAnalyzerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<HTMLIFrameElement>(null);
  const borrower = borrowerDisplayName(loan) || 'Unnamed borrower';

  useLayoutEffect(() => {
    const fit = () => {
      const el = containerRef.current;
      if (!el) {
        return;
      }
      const top = Math.max(0, Math.round(el.getBoundingClientRect().top));
      el.style.height = `calc(100dvh - ${top}px)`;
    };
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, []);

  return (
    <div ref={containerRef} className="flex h-screen min-h-[28rem] flex-col bg-background">
      <div className="flex h-11 flex-shrink-0 items-center gap-2 border-b bg-card px-2 lg:px-4">
        <Button variant="ghost" size="sm" className="gap-1" onClick={onChangeLoan}>
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          Change loan
        </Button>
        <p className="min-w-0 truncate text-sm">
          <span className="font-semibold text-foreground">{borrower}</span>
          <span className="text-muted-foreground">
            {' '}· Loan {loan.trans_details_loan_number || '—'}
            {loan.milestone ? ` · ${loan.milestone}` : ''}
          </span>
        </p>
      </div>
      <iframe
        ref={frameRef}
        key={loan.id}
        src={buildAnalyzerEmbedUrl(loan)}
        title={`Bank statement analyzer for ${borrower}`}
        className="w-full flex-1 border-0 bg-background"
        referrerPolicy="same-origin"
      />
      <AnalyzerEfolderBridge frameRef={frameRef} />
    </div>
  );
}

export function AnalyzerWorkspace({ onBack }: AnalyzerWorkspaceProps) {
  const { user } = useAuth();
  const organizationId = isExtendedUser(user) ? user.organization_id : undefined;
  const scope = useLoanCenterScope();
  const { availability, check } = useAnalyzerAvailability();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLoan, setSelectedLoan] = useState<AnalyzerLoan | null>(null);
  const searchId = useId();
  const { loans, isLoading, error } = useAnalyzerLoans(
    scope.canAccess ? organizationId : undefined,
    searchQuery
  );

  if (selectedLoan) {
    return <EmbeddedAnalyzer loan={selectedLoan} onChangeLoan={() => setSelectedLoan(null)} />;
  }

  return (
    <div className="min-h-full bg-background">
      <div className="border-b bg-card px-4 py-2 lg:px-6">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" className="-ml-2 gap-1" onClick={onBack}>
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            Back
          </Button>
          <div className="min-w-0">
            <h1 className="text-lg font-semibold text-foreground">Bank Statement Analyzer</h1>
            <p className="text-sm text-muted-foreground">Search for a loan to analyze its bank statements</p>
          </div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-3xl space-y-4 p-4 lg:p-6">
        {availability === 'offline' && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" aria-hidden="true" />
            <AlertTitle>Bank statement analyzer is temporarily unavailable</AlertTitle>
            <AlertDescription className="space-y-2">
              <p>You can still search for a loan. Check again before opening its bank statements.</p>
              <Button variant="outline" size="sm" className="gap-1" onClick={() => void check()}>
                <RefreshCw className="h-4 w-4" aria-hidden="true" />
                Check again
              </Button>
            </AlertDescription>
          </Alert>
        )}

        <LoanPicker
          searchId={searchId}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          scopeLoading={scope.isLoading}
          scopeMode={scope.mode}
          scopeError={scope.error}
          hasOrganization={Boolean(organizationId)}
          loans={loans}
          isLoading={isLoading}
          error={error}
          disabled={availability !== 'online'}
          onSelect={setSelectedLoan}
        />
      </div>
    </div>
  );
}

interface LoanPickerProps {
  searchId: string;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  scopeLoading: boolean;
  scopeMode: string;
  scopeError: Error | null;
  hasOrganization: boolean;
  loans: AnalyzerLoan[];
  isLoading: boolean;
  error: string | null;
  disabled: boolean;
  onSelect: (loan: AnalyzerLoan) => void;
}

function LoanPicker({
  searchId,
  searchQuery,
  onSearchChange,
  scopeLoading,
  scopeMode,
  scopeError,
  hasOrganization,
  loans,
  isLoading,
  error,
  disabled,
  onSelect
}: LoanPickerProps) {
  if (scopeLoading) {
    return <p className="text-sm text-muted-foreground" role="status">Checking your loan access…</p>;
  }
  if (scopeMode === 'unconfigured') {
    return (
      <Alert>
        <AlertTitle>Encompass account not linked</AlertTitle>
        <AlertDescription>
          Your profile has no Encompass user ID, so no loans can be listed. Ask an administrator to link your Encompass account.
        </AlertDescription>
      </Alert>
    );
  }
  if (scopeError || scopeMode === 'denied' || !hasOrganization) {
    return (
      <Alert variant="destructive">
        <AlertTitle>No loan access</AlertTitle>
        <AlertDescription>Your account does not have access to loans.</AlertDescription>
      </Alert>
    );
  }

  return (
    <section className="mx-auto w-full max-w-xl" aria-label="Find a loan">
      <Label htmlFor={searchId} className="mb-1 block">Search loans</Label>
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
        <Input
          id={searchId}
          type="text"
          role="searchbox"
          value={searchQuery}
          onChange={(event) => onSearchChange(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              onSearchChange('');
            }
          }}
          placeholder="Search by borrower name or loan #…"
          className="pl-9 pr-10"
          autoComplete="off"
          aria-describedby={`${searchId}-hint`}
        />
        {searchQuery && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="absolute right-0 top-0 h-10 w-10"
            aria-label="Clear loan search"
            onClick={() => {
              onSearchChange('');
              document.getElementById(searchId)?.focus();
            }}
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </Button>
        )}
      </div>
      <p id={`${searchId}-hint`} className="mt-2 text-xs text-muted-foreground">
        Enter at least {ANALYZER_LOAN_SEARCH_MIN_LENGTH} characters to find an active loan or prospect
        {scopeMode === 'assigned' ? ' assigned to you.' : ' you can access.'}
      </p>

      <div className="mt-3" aria-live="polite">
        {searchQuery.trim().length < ANALYZER_LOAN_SEARCH_MIN_LENGTH ? null : isLoading ? (
          <p className="py-4 text-sm text-muted-foreground" role="status">Searching loans…</p>
        ) : error ? (
          <p className="py-6 text-center text-sm text-destructive" role="alert">Loans could not be loaded. {error}</p>
        ) : loans.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            No active loans or prospects match your search.
          </p>
        ) : (
          <ul className="max-h-80 divide-y overflow-y-auto rounded-md border bg-card shadow-sm" aria-label="Matching loans">
            {loans.map((loan) => {
              const borrower = borrowerDisplayName(loan) || 'Unnamed borrower';
              const officer = loanOfficerDisplayName(loan);
              return (
                <li key={loan.id}>
                  <button
                    type="button"
                    disabled={disabled}
                    onClick={() => onSelect(loan)}
                    aria-label={`Analyze bank statements for ${borrower}, loan ${loan.trans_details_loan_number || 'without a number'}`}
                    className="flex w-full flex-wrap items-center justify-between gap-x-4 gap-y-1 px-3 py-3 text-left transition-colors hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <span className="min-w-0 flex-1 basis-40">
                      <span className="block truncate text-sm font-medium text-foreground">{borrower}</span>
                      <span className="block truncate text-xs text-muted-foreground">
                        Loan {loan.trans_details_loan_number || '—'}
                        {officer ? ` · ${officer}` : ''}
                      </span>
                    </span>
                    {loan.milestone && (
                      <span className="shrink-0 rounded-full border px-2 py-0.5 text-xs text-muted-foreground">{loan.milestone}</span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}
